/**
 * ============================================================================
 * MENU BAR COMPONENT
 * ============================================================================
 *
 * A macOS-style menu bar with full menus: System, App Name, File, Edit,
 * View, Window, Help. Supports hover-to-switch between open menus.
 *
 * @packageDocumentation
 */

"use client";

import { useCallback, useEffect, useState, useRef, useMemo } from "react";
import { WINDOW_ID } from "../apps/registry";
import { useDesktopContext } from "../contexts/DesktopContext";
import { useThemeOptional } from "../contexts/ThemeContext";
import { NotificationBell, NotificationPanel } from "./NotificationCenter";
import { NotificationWidgetPanel } from "./NotificationWidgetPanel";
import { useMediaPermissions } from "../hooks/useMediaPermissions";
import type { MediaPermissionState } from "../hooks/useMediaPermissions";
import type { WindowInstance } from "../types";

/* ==========================================================================
   TYPES
   ========================================================================== */

export interface MenuBarProps {
  /** Height of the menu bar in pixels */
  height?: number;
  /** Callback to open spotlight search */
  onOpenSpotlight?: () => void;
  /** Callback to open the guided feature tour (Help → Tutorial) */
  onOpenTutorial?: () => void;
  /** Callback to trigger the hurricane easter egg */
  onTriggerHurricane?: () => void;
  /** Callback to trigger the fire easter egg */
  onTriggerFire?: () => void;
  /** Callback to trigger the war easter egg */
  onTriggerWar?: () => void;
  /** Callback to trigger the blizzard easter egg */
  onTriggerBlizzard?: () => void;
  /** Callback to trigger the LSD easter egg */
  onTriggerLsd?: () => void;
  /** Callback to trigger Theme Time Travel */
  onTriggerTimeTravel?: () => void;
  /** Callback to toggle the widget layer */
  onToggleWidgets?: () => void;
  /** Callback to toggle the app switcher overlay */
  onToggleAppSwitcher?: () => void;
  /** Whether the app switcher is currently open (drives the button's pressed state) */
  appSwitcherOpen?: boolean;
  /** Whether the menu bar is auto-hidden (fullscreen mode) */
  autoHidden?: boolean;
  /** Mouse enter handler (for hover-reveal in fullscreen) */
  onMouseEnter?: () => void;
  /** Mouse leave handler (for hover-reveal in fullscreen) */
  onMouseLeave?: () => void;
}

type NetworkEffectiveType = "slow-2g" | "2g" | "3g" | "4g";

interface NetworkStatus {
  isOnline: boolean;
  effectiveType: NetworkEffectiveType | null;
  downlink: number | null;
}

interface NetworkInformation extends EventTarget {
  effectiveType?: NetworkEffectiveType;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
    mozConnection?: NetworkInformation;
    webkitConnection?: NetworkInformation;
  }
}

type MenuKey = "system" | "file" | "edit" | "view" | "window" | "help";

type HelpTab = "search" | "shortcuts" | "windows" | "desktop" | "apps" | "tips";

interface MenuItem {
  label: string;
  shortcut?: string;
  onClick?: () => void;
  disabled?: boolean;
  checked?: boolean;
  diamond?: boolean;
  divider?: boolean;
  hidden?: boolean;
  submenu?: MenuItem[];
}

/* ==========================================================================
   HOOKS
   ========================================================================== */

function useNetworkStatus(): NetworkStatus {
  const [status, setStatus] = useState<NetworkStatus>(() => {
    if (typeof navigator === "undefined") {
      return { isOnline: true, effectiveType: null, downlink: null };
    }
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;
    return {
      isOnline: navigator.onLine,
      effectiveType:
        (connection?.effectiveType as NetworkEffectiveType) ?? null,
      downlink: connection?.downlink ?? null,
    };
  });

  useEffect(() => {
    const updateOnlineStatus = () => {
      setStatus((prev) => ({ ...prev, isOnline: navigator.onLine }));
    };

    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    const updateConnectionStatus = () => {
      if (connection) {
        setStatus((prev) => ({
          ...prev,
          effectiveType:
            (connection.effectiveType as NetworkEffectiveType) ?? null,
          downlink: connection.downlink ?? null,
        }));
      }
    };

    window.addEventListener("online", updateOnlineStatus);
    window.addEventListener("offline", updateOnlineStatus);

    if (connection) {
      connection.addEventListener("change", updateConnectionStatus);
    }

    return () => {
      window.removeEventListener("online", updateOnlineStatus);
      window.removeEventListener("offline", updateOnlineStatus);
      if (connection) {
        connection.removeEventListener("change", updateConnectionStatus);
      }
    };
  }, []);

  return status;
}

/* ==========================================================================
   HELPERS
   ========================================================================== */

function formatMacOSTime(date: Date, compact = false): string {
  let hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;

  const minutesStr = minutes < 10 ? `0${minutes}` : minutes;

  if (compact) {
    return `${hours}:${minutesStr} ${ampm}`;
  }

  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const dayName = days[date.getDay()];
  const monthName = months[date.getMonth()];
  const dayNum = date.getDate();

  return `${dayName} ${monthName} ${dayNum}  ${hours}:${minutesStr} ${ampm}`;
}

/* ==========================================================================
   PRIVATE COMPONENTS
   ========================================================================== */

interface MenuItemButtonProps {
  item: MenuItem;
  isDark: boolean;
  onClose: () => void;
  isMobile?: boolean;
}

function MenuItemButton({
  item,
  isDark,
  onClose,
  isMobile,
}: MenuItemButtonProps) {
  if (item.divider) {
    return (
      <div
        className="mx-2 my-1"
        style={{
          height: "1px",
          backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
        }}
      />
    );
  }

  if (item.hidden) return null;

  const fontSize = isMobile ? "16px" : "13px";

  return (
    <button
      onClick={() => {
        if (!item.disabled && item.onClick) {
          item.onClick();
          onClose();
        }
      }}
      className={`flex w-full items-center gap-1.5 px-3 text-left transition-colors ${isMobile ? "py-2.5" : "py-1.5"}`}
      style={{
        fontSize,
        color: item.disabled
          ? isDark
            ? "rgba(255,255,255,0.3)"
            : "rgba(0,0,0,0.25)"
          : isDark
            ? "rgba(255,255,255,0.9)"
            : "rgba(0,0,0,0.85)",
        cursor: item.disabled ? "default" : "pointer",
      }}
      onMouseEnter={(e) => {
        if (!item.disabled) {
          e.currentTarget.style.backgroundColor = isDark
            ? "rgba(59, 130, 246, 0.5)"
            : "rgba(59, 130, 246, 0.2)";
          e.currentTarget.style.color = isDark ? "#fff" : "rgba(0,0,0,0.95)";
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent";
        e.currentTarget.style.color = item.disabled
          ? isDark
            ? "rgba(255,255,255,0.3)"
            : "rgba(0,0,0,0.25)"
          : isDark
            ? "rgba(255,255,255,0.9)"
            : "rgba(0,0,0,0.85)";
      }}
      disabled={item.disabled}
    >
      {(item.checked || item.diamond) && (
        <span className="flex-shrink-0 text-xs">
          {item.checked ? "✓" : "◆"}
        </span>
      )}
      <span className="flex-1">{item.label}</span>
      {item.shortcut && (
        <span
          className="ml-4 flex-shrink-0 text-xs"
          style={{
            color: item.disabled
              ? isDark
                ? "rgba(255,255,255,0.2)"
                : "rgba(0,0,0,0.15)"
              : isDark
                ? "rgba(255,255,255,0.4)"
                : "rgba(0,0,0,0.35)",
          }}
        >
          {item.shortcut}
        </span>
      )}
    </button>
  );
}

interface MenuDropdownProps {
  items: MenuItem[];
  isDark: boolean;
  onClose: () => void;
  alignRight?: boolean;
  isMobile?: boolean;
}

function MenuDropdown({
  items,
  isDark,
  onClose,
  alignRight,
  isMobile,
}: MenuDropdownProps) {
  const [submenuStack, setSubmenuStack] = useState<
    { label: string; items: MenuItem[] }[]
  >([]);
  const activeItems =
    submenuStack.length > 0
      ? submenuStack[submenuStack.length - 1].items
      : items;

  const fontSize = isMobile ? "16px" : "13px";

  return (
    <div
      className="absolute top-full mt-0 min-w-[220px] overflow-y-auto rounded-md py-1 shadow-xl"
      style={{
        backgroundColor: isDark
          ? "rgba(40, 40, 40, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
        border: isDark
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.15)",
        backdropFilter: "blur(20px)",
        maxHeight: isMobile ? "75vh" : "calc(100vh - 32px)",
        ...(alignRight ? { right: 0 } : { left: 0 }),
      }}
    >
      {submenuStack.length > 0 && (
        <button
          onClick={() => setSubmenuStack((s) => s.slice(0, -1))}
          className={`flex w-full items-center gap-1.5 px-3 text-left transition-colors ${isMobile ? "py-2.5" : "py-1.5"}`}
          style={{
            fontSize,
            color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.4)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = isDark
              ? "rgba(59, 130, 246, 0.5)"
              : "rgba(59, 130, 246, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <span className="flex-shrink-0 text-xs">‹</span>
          <span className="flex-1">
            {submenuStack[submenuStack.length - 1].label}
          </span>
        </button>
      )}
      {submenuStack.length > 0 && (
        <div
          className="mx-2 my-1"
          style={{
            height: "1px",
            backgroundColor: isDark
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.1)",
          }}
        />
      )}
      {activeItems.map((item, i) =>
        item.submenu ? (
          <button
            key={i}
            onClick={() =>
              setSubmenuStack((s) => [
                ...s,
                { label: item.label, items: item.submenu! },
              ])
            }
            className={`flex w-full items-center gap-1.5 px-3 text-left transition-colors ${isMobile ? "py-2.5" : "py-1.5"}`}
            style={{
              fontSize,
              color: isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)",
              cursor: "pointer",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = isDark
                ? "rgba(59, 130, 246, 0.5)"
                : "rgba(59, 130, 246, 0.2)";
              e.currentTarget.style.color = isDark
                ? "#fff"
                : "rgba(0,0,0,0.95)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color = isDark
                ? "rgba(255,255,255,0.9)"
                : "rgba(0,0,0,0.85)";
            }}
          >
            <span className="flex-1">{item.label}</span>
            <span
              className="flex-shrink-0 text-xs"
              style={{
                color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)",
              }}
            >
              ›
            </span>
          </button>
        ) : (
          <MenuItemButton
            key={i}
            item={item}
            isDark={isDark}
            onClose={onClose}
            isMobile={isMobile}
          />
        )
      )}
    </div>
  );
}

/* ==========================================================================
   HELP OVERLAY
   ========================================================================== */

interface HelpShortcutRowProps {
  keys: string;
  desc: string;
  isDark: boolean;
}

function HelpShortcutRow({ keys, desc, isDark }: HelpShortcutRowProps) {
  return (
    <div className="flex items-center justify-between px-1 py-1.5">
      <span
        className="text-[13px]"
        style={{ color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.75)" }}
      >
        {desc}
      </span>
      <kbd
        className="ml-4 flex-shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px]"
        style={{
          backgroundColor: isDark
            ? "rgba(255,255,255,0.08)"
            : "rgba(0,0,0,0.06)",
          color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
          border: isDark
            ? "1px solid rgba(255,255,255,0.1)"
            : "1px solid rgba(0,0,0,0.1)",
        }}
      >
        {keys}
      </kbd>
    </div>
  );
}

interface HelpSectionHeaderProps {
  title: string;
  isDark: boolean;
}

function HelpSectionHeader({ title, isDark }: HelpSectionHeaderProps) {
  return (
    <h3
      className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wider first:mt-0"
      style={{ color: isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.35)" }}
    >
      {title}
    </h3>
  );
}

interface HelpTipProps {
  text: string;
  isDark: boolean;
}

function HelpTip({ text, isDark }: HelpTipProps) {
  return (
    <div
      className="px-1 py-1.5 text-[13px]"
      style={{ color: isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.75)" }}
    >
      {text}
    </div>
  );
}

type HelpContentTab = Exclude<HelpTab, "search">;

type HelpItem =
  | { kind: "shortcut"; keys: string; desc: string }
  | { kind: "tip"; text: string };

interface HelpSection {
  title: string;
  items: HelpItem[];
}

const s = (keys: string, desc: string): HelpItem => ({
  kind: "shortcut",
  keys,
  desc,
});
const t = (text: string): HelpItem => ({ kind: "tip", text });

const HELP_CONTENT: Record<HelpContentTab, HelpSection[]> = {
  shortcuts: [
    {
      title: "Global",
      items: [
        s("⌘⇧S", "Open Spotlight search"),
        s("⌘W", "Close active window"),
        s("⌘M", "Minimize active window"),
        s("⌃⌘F", "Toggle full screen"),
        s("F4", "Toggle widgets dashboard"),
        s("⌘⇧A", "Toggle app switcher"),
      ],
    },
    {
      title: "For Fun",
      items: [
        s("⌘H", "Hurricane mode"),
        s("⌘FF", "Fire mode"),
        s("⌘LL", "War mode"),
        s("⌘BB", "Blizzard mode"),
        s("⌘DD", "LSD mode"),
        s("⌘JJ", "Theme Time Travel"),
      ],
    },
    {
      title: "Text Editing",
      items: [
        s("⌘Z", "Undo"),
        s("⇧⌘Z", "Redo"),
        s("⌘X / ⌘C / ⌘V", "Cut / Copy / Paste"),
        s("⌘A", "Select all"),
      ],
    },
    {
      title: "Calculator",
      items: [
        s("0-9 . + - * /", "Input & operators"),
        s("Enter / =", "Calculate result"),
        s("Escape", "Clear all"),
        s("Backspace", "Clear entry"),
      ],
    },
    {
      title: "BScode",
      items: [
        s("⌘S", "Save file"),
        s("⌘O", "Open file"),
        s("⌘N", "New file"),
        s("⌘W", "Close tab"),
        s("⌘B", "Toggle sidebar"),
        s("Tab / ⇧Tab", "Indent / Outdent"),
      ],
    },
    {
      title: "Terminal",
      items: [s("Enter", "Execute command"), s("↑ / ↓", "Command history")],
    },
    {
      title: "Spreadsheet",
      items: [
        s("Tab / ⇧Tab", "Move right / left"),
        s("Arrow keys", "Navigate cells"),
        s("⇧+Arrow", "Extend selection"),
        s("⌘Z / ⇧⌘Z", "Undo / Redo"),
        s("⌘A", "Select all cells"),
      ],
    },
    {
      title: "Stage (Presentations)",
      items: [
        s("⌘D", "Duplicate element"),
        s("→ / Space", "Next slide"),
        s("←", "Previous slide"),
        s("Escape", "Exit presentation"),
      ],
    },
    { title: "Xylophone", items: [s("1 - 8", "Play notes")] },
    {
      title: "Arcade Games",
      items: [
        s("← → ↑ ↓ / WASD", "Movement"),
        s("Space", "Fire / Hard drop / Start"),
        s("Enter", "Start / Restart"),
      ],
    },
  ],
  windows: [
    {
      title: "Moving & Resizing",
      items: [
        t("Drag the title bar to move a window."),
        t("Drag any edge or corner to resize."),
        t("Double-click the title bar to maximize or restore."),
      ],
    },
    {
      title: "Window Snapping",
      items: [
        t("Drag to the top edge of the screen to maximize."),
        t("Drag to the left or right edge to snap to half-screen."),
        t("Dragging away from a snapped position restores the previous size."),
      ],
    },
    {
      title: "Minimize & Restore",
      items: [
        t("Click the yellow button (or ⌘M) to minimize to the dock."),
        t("Click a minimized app's dock icon to restore it."),
        t(
          "The Window menu shows all open/minimized windows — ✓ = active, ◆ = minimized."
        ),
      ],
    },
    {
      title: "Focus & Z-Order",
      items: [
        t("Click anywhere on a window to bring it to the front."),
        t("Click the app name in the menu bar to re-focus the active window."),
        t("Click the desktop background to deactivate all windows."),
      ],
    },
    {
      title: "Mobile",
      items: [t("On mobile, all windows automatically open maximized.")],
    },
  ],
  desktop: [
    {
      title: "Dock",
      items: [
        t("Click a dock icon to open or focus its window."),
        t(
          "If the window is already open and active, clicking the icon will minimize it."
        ),
        t("Drag icons in the dock to reorder them."),
        t("Drag an icon off the dock to move it to the desktop."),
        t("A green dot below an icon means the app is running."),
      ],
    },
    {
      title: "Right-Click Menu",
      items: [
        t("Right-click (or long-press on mobile) a dock icon for options."),
        t(
          "'Move to Desktop' places the icon freely on the desktop background."
        ),
        t("'Move to Dock' returns a desktop icon back to the dock bar."),
      ],
    },
    {
      title: "Layout Modes",
      items: [
        t(
          "Open Settings to switch icon layout: Dock (bottom), Left, Right, or Free."
        ),
        t("In Free mode, icons can be dragged anywhere on the desktop."),
      ],
    },
    {
      title: "Spotlight Search",
      items: [
        t(
          "Press ⌘⇧S to open Spotlight. Type to filter apps, use arrow keys to navigate, Enter to open."
        ),
        t("Spotlight shows a 'Running' label and green dot for open apps."),
      ],
    },
    {
      title: "Widgets",
      items: [
        t("Press F4 or use View → Widgets to toggle the widget dashboard."),
        t(
          "Drag widgets to reposition them. Drag the bottom-right corner to resize."
        ),
        t(
          "Close individual widgets with ✕, then use 'Show All' to bring them back."
        ),
        t(
          "Includes Clock, Calendar, and System stats. Positions persist across sessions."
        ),
      ],
    },
  ],
  apps: [
    {
      title: "Terminal",
      items: [
        t(
          "A virtual filesystem with commands: ls, cd, pwd, mkdir, touch, rm, cat, echo, help, and more."
        ),
        t(
          "Files deleted with 'rm' are moved to ~/.Trash, not permanently deleted."
        ),
        t("The filesystem is shared with Scout and BScode."),
      ],
    },
    {
      title: "Scout & BScode",
      items: [
        t("Double-click a file in Scout to open it in BScode."),
        t(
          "BScode supports syntax highlighting for JS, TS, JSON, HTML, CSS, Python, and Markdown."
        ),
        t("The Trash icon opens Scout at the ~/.Trash directory."),
      ],
    },
    {
      title: "Calculator",
      items: [
        t(
          "Toggle between Simple and Scientific modes for advanced math (sin, cos, tan, factorial, etc.)."
        ),
        t("Full keyboard support — type numbers and operators directly."),
      ],
    },
    {
      title: "Spreadsheet (Ledger)",
      items: [
        t(
          "Supports formulas: =SUM(A1:A5), =AVG(...), =COUNT(...), and cell references like A1."
        ),
        t("Shift-click to extend a selection. Shift+Arrow to select ranges."),
      ],
    },
    {
      title: "Presentations (Stage)",
      items: [
        t(
          "⌘D duplicates the selected element. Enter presentation mode for full-screen slides."
        ),
        t(
          "In presentation mode, the cursor auto-hides after 3 seconds of inactivity."
        ),
      ],
    },
    {
      title: "Rich Text Editor (Quill)",
      items: [
        t(
          "Supports inline editing, file attachments via drag-and-drop, and find & replace."
        ),
        t("Documents are saved to the virtual filesystem."),
      ],
    },
    {
      title: "Python REPL",
      items: [
        t(
          "Runs Python in your browser via Pyodide (WebAssembly). Supports numpy, pandas, and more."
        ),
      ],
    },
    {
      title: "Minesweeper",
      items: [
        t(
          "Right-click (or long-press) to flag a cell. Configurable grid size and three difficulty levels."
        ),
        t("Best times are saved per difficulty and persist across sessions."),
      ],
    },
    {
      title: "Arcade Games",
      items: [
        t("PacMan: Arrow keys or WASD to move. Eat all dots to win."),
        t(
          "Tetris: Arrow keys to move/rotate, Space for hard drop (+2 pts per row), Down for soft drop (+1 pt per row)."
        ),
        t(
          "Galaga: Arrows or A/D to move, Space to fire. Touch controls available on mobile."
        ),
        t(
          "Tetris supports wall kicks — pieces auto-adjust when rotating near edges."
        ),
      ],
    },
    {
      title: "Xylophone",
      items: [
        t(
          "Play notes with keys 1–8 or tap/click the bars. Toggle the flip switch to reverse the key mapping."
        ),
        t(
          "On mobile, tap a bar once to enable audio (browser requirement), then play normally."
        ),
      ],
    },
    {
      title: "Social Media",
      items: [
        t(
          "Enter sends a message. Shift+Enter adds a new line without sending."
        ),
      ],
    },
    {
      title: "Notes",
      items: [
        t("A simple notepad with a sidebar for managing multiple notes."),
        t(
          "Double-click a note title in the sidebar to rename it. Drag the sidebar edge to resize."
        ),
      ],
    },
    {
      title: "Paint",
      items: [
        t(
          "Drawing tools: brush, eraser, line, rectangle, circle, flood fill, and text."
        ),
        t(
          "Customizable colors, brush sizes, and shape fill modes. Drawings are saved as PNG to the filesystem."
        ),
      ],
    },
    {
      title: "Clock",
      items: [
        t(
          "Three tabs: Stopwatch with lap tracking, Timer with quick presets (1–60 min), and Alarm with recurring days."
        ),
        t("Desktop notifications fire when timers complete or alarms ring."),
      ],
    },
    {
      title: "Weather",
      items: [
        t(
          "Current conditions, hourly forecast (24h), and 7-day forecast. Toggle °C / °F."
        ),
        t(
          "Search locations, save favorites, set a default, or use auto-location detection."
        ),
      ],
    },
    {
      title: "Activity Monitor",
      items: [
        t(
          "Sortable process table with CPU%, memory, threads, and status. Search to filter processes."
        ),
        t(
          "Tabs for CPU, memory pressure, disk I/O, network, and energy impact with sparkline graphs."
        ),
      ],
    },
    {
      title: "Dev Tools",
      items: [
        t(
          "Six utilities: JSON formatter, diff viewer, timestamp converter, color converter, UUID/SHA generator, and YAML↔JSON converter."
        ),
      ],
    },
    {
      title: "Sandbox",
      items: [
        t(
          "Live code sandbox with React, Vue, and Angular templates. Includes file explorer, editor, preview, and console."
        ),
        t("Save and load code snippets to persistent storage."),
      ],
    },
    {
      title: "Russian Roulette",
      items: [
        t(
          "Revolver simulator with interactive cylinder rotation, trigger pull, and chamber tracking."
        ),
      ],
    },
    {
      title: "Clipboard Manager",
      items: [
        t(
          "Automatically captures text you copy or cut anywhere on the desktop."
        ),
        t(
          "Entries are categorized as Text, Code, or URL. Search, filter by category, and pin important items."
        ),
        t(
          "Click any entry to re-copy it. Pinned items survive 'Clear'. History persists across sessions (up to 100 entries)."
        ),
      ],
    },
    {
      title: "JavaScript REPL",
      items: [
        t(
          "Run JavaScript in a sandboxed web worker with command history and console output."
        ),
        t(
          "Access the virtual filesystem via the `vfs` global — read and write files shared with Terminal, Scout, and BScode."
        ),
      ],
    },
    {
      title: "Chat Jippity",
      items: [
        t(
          "An AI chat assistant that can read your files, edit code, and drive other apps via tool calls."
        ),
        t(
          "Supports voice input/output and renders code artifacts inline in the chat."
        ),
      ],
    },
    {
      title: "Messages",
      items: [
        t(
          "Direct messaging with friends — typing indicators, online presence, and auto-linkified URLs."
        ),
        t(
          "Enter to send, Shift+Enter for a new line. Links to posts within the app are detected automatically."
        ),
      ],
    },
    {
      title: "FaceCall",
      items: [
        t(
          "WebRTC video calls with friends — includes call history, presence, and live duration tracking."
        ),
        t(
          "Camera/mic requires HTTPS or localhost. On plain HTTP the browser will block getUserMedia."
        ),
      ],
    },
    {
      title: "Fake Call",
      items: [
        t(
          "Simulate an incoming call from a custom contact — pick a ringtone, schedule a delay, and escape awkward situations."
        ),
      ],
    },
    {
      title: "Music",
      items: [
        t(
          "Save YouTube music links, organize them into playlists, and play back from a persistent library."
        ),
      ],
    },
    {
      title: "Watch",
      items: [
        t(
          "Bookmark and stream YouTube videos with channel info and playlist management."
        ),
      ],
    },
    {
      title: "Photo Booth",
      items: [
        t(
          "Capture webcam photos with filters (sepia, blur, vintage, contrast) and a countdown timer."
        ),
        t(
          "Saved photos live in a persistent gallery with mirror toggle and filter adjustments."
        ),
      ],
    },
    {
      title: "Fractal",
      items: [
        t(
          "An interactive museum of 3D fractal exhibits — rotate, zoom, and read placards for each."
        ),
      ],
    },
    {
      title: "Markdown Preview",
      items: [
        t(
          "Split-view Markdown editor with live rendering and syntax-highlighted code blocks."
        ),
        t(
          "Documents auto-save locally; create and switch between multiple files."
        ),
      ],
    },
    {
      title: "Mappuccino",
      items: [
        t(
          "OpenStreetMap-powered maps with turn-by-turn directions, saved pins, and multi-stop trip planning."
        ),
        t(
          "GPS-style navigation with audio cues; saved locations persist across sessions."
        ),
      ],
    },
    {
      title: "Bible",
      items: [
        t(
          "Read the Bible with multiple translations and chapter/verse navigation."
        ),
        t(
          "Desktop shows a dual-page spread; mobile uses page-flip animations."
        ),
      ],
    },
    {
      title: "Stonks",
      items: [
        t(
          "Track stock prices and build a watchlist with live-ish market data."
        ),
        t(
          "Toggle between Market view (discovery) and Portfolio view (your holdings and lots)."
        ),
      ],
    },
    {
      title: "YesQL (Query Runner)",
      items: [
        t(
          "Run SQLite queries in-browser against databases persisted via OPFS — fully offline."
        ),
        t(
          "Create, import, and manage multiple DBs; syntax highlighting on the editor and downloadable result sets."
        ),
        t(
          "Drag the editor/results divider to resize — the split is remembered per session."
        ),
      ],
    },
    {
      title: "App Store",
      items: [
        t(
          "Browse, install, and uninstall desktop apps by category. System apps can't be removed."
        ),
      ],
    },
    {
      title: "Issues",
      items: [
        t(
          "File bug reports and feature requests with categories, status, comments, and image attachments."
        ),
      ],
    },
    {
      title: "Desktop Pets",
      items: [
        t(
          "Adopt virtual pets that need feeding and care — neglect them and they'll decay."
        ),
        t(
          "Breed pets with friends, gift them, and watch the lineage tree branch out with crossbreed variants."
        ),
        t(
          "Pets roam on the Pet Yard — a separate window that shows your living collection."
        ),
      ],
    },
    {
      title: "Desktop Plants",
      items: [
        t(
          "Grow virtual plants with water, light, and time — miss care windows and they'll wither."
        ),
        t(
          "Plants live in the Plant Garden window; you can also gift them to friends."
        ),
      ],
    },
    {
      title: "Farmer's Tan",
      items: [
        t(
          "A deep farming sim: crops, animals, fishing, crafting, seasons, weather, and staff management."
        ),
        t(
          "Progress persists across sessions — research trees, achievements, and contracts carry over."
        ),
      ],
    },
    {
      title: "Chess",
      items: [
        t(
          "Play AI bots of varying strength or challenge friends in real-time multiplayer."
        ),
        t(
          "Includes move classification, evaluation bar, and game history with replay."
        ),
      ],
    },
    {
      title: "Casino",
      items: [
        t(
          "Blackjack, Texas Hold'em, slots, roulette, and more — all on virtual chips."
        ),
        t(
          "Some tables support real-time multiplayer so you can compete with other players."
        ),
      ],
    },
    {
      title: "Typoholic",
      items: [
        t(
          "Typing trainer with code snippets and prose passages — tracks WPM, accuracy, and errors."
        ),
        t(
          "Choose split or stacked layout; recent runs save to a local history."
        ),
      ],
    },
    {
      title: "Helicopter",
      items: [
        t(
          "Fly a chopper through a scrolling cave — tap/hold to ascend, release to drop."
        ),
        t(
          "The cave narrows and obstacles ramp as time passes. Scores are saved locally."
        ),
      ],
    },
    {
      title: "Line Rider",
      items: [
        t(
          "Sketch physics-based tracks with pencil, line, and eraser tools, then watch the sled ride them."
        ),
        t(
          "Mix solid lines, acceleration zones, and scenery lines; switch to play mode to run the simulation."
        ),
      ],
    },
    {
      title: "Perfect Circle",
      items: [
        t(
          "Draw a circle freehand — scored on radius consistency and angular coverage."
        ),
        t(
          "Square mode available for a different shape challenge; local score history."
        ),
      ],
    },
    {
      title: "Huntin' Season",
      items: [
        t(
          "Canvas hunting game with multiple animal types, difficulty levels, and rifle sound synthesis."
        ),
      ],
    },
    {
      title: "Fish On!",
      items: [
        t(
          "Fish a variety of species with progressive difficulty; leaderboards track your biggest hauls."
        ),
      ],
    },
    {
      title: "Range Day",
      items: [
        t(
          "Target shooting range with handguns, revolvers, and rifles — each with their own recoil and audio."
        ),
      ],
    },
    {
      title: "Snipe City",
      items: [
        t(
          "Long-range urban sniping with single-player and co-op 'Crew Raid' modes."
        ),
      ],
    },
    {
      title: "Grill Master",
      items: [
        t(
          "Cook meat to temperature, season correctly, and fill orders under the clock."
        ),
      ],
    },
    {
      title: "Bar Man",
      items: [
        t(
          "Mix cocktails by pouring ingredients in the right order to fill multiple customer orders at once."
        ),
      ],
    },
    {
      title: "Yard Work",
      items: [
        t(
          "Mow the lawn level by level — dodge obstacles and manage stamina. Joystick or arrow controls."
        ),
      ],
    },
    {
      title: "Pressure's On",
      items: [
        t(
          "Pressure-wash grime off surfaces — adjust the nozzle and use the minimap to navigate levels."
        ),
      ],
    },
    {
      title: "Ice Urinal",
      items: [
        t(
          "Exactly what it says. Stream water to carve ice with physics-y particles and Web Audio SFX."
        ),
      ],
    },
  ],
  tips: [
    {
      title: "Menu Bar",
      items: [
        t(
          "The app name in the menu bar updates to show the active window's title."
        ),
        t(
          "Click an open menu, then hover over other menus to switch between them."
        ),
        t(
          "The Wi-Fi icon (top-right) shows your real network status and connection speed."
        ),
        t(
          "The clock shows full date and time on desktop, time only on mobile."
        ),
      ],
    },
    {
      title: "Cross-App Integration",
      items: [
        t(
          "Terminal, Scout, BScode, Quill, and Trash all share the same virtual filesystem."
        ),
        t(
          "Create a file in Terminal ('touch hello.txt'), then open it in Scout and edit it in BScode."
        ),
        t(
          "Scout auto-refreshes every 2 seconds to pick up filesystem changes."
        ),
        t(
          "Clipboard Manager records everything you copy/cut across all apps — open it to search and re-use past clips."
        ),
      ],
    },
    {
      title: "Window Persistence",
      items: [
        t(
          "Window positions and sizes are saved. Reopening a window restores its previous geometry."
        ),
        t(
          "New center-positioned windows cascade slightly so they don't stack directly on top of each other."
        ),
      ],
    },
    {
      title: "Accounts & Roles",
      items: [
        t(
          "Guest mode gives you full access to all local apps without signing in."
        ),
        t("Signing in unlocks social features and data synced to the cloud."),
        t(
          "The Admin icon and menu item only appear for users with the admin role."
        ),
      ],
    },
    {
      title: "Mobile",
      items: [
        t(
          "Long-press is the mobile equivalent of right-click (for flags, context menus)."
        ),
        t(
          "Arcade games show on-screen touch controls automatically on mobile."
        ),
        t(
          "The menu bar collapses on small screens — only the system menu and clock are shown."
        ),
      ],
    },
  ],
};

const HELP_TAB_LABELS: Record<HelpContentTab, string> = {
  shortcuts: "Shortcuts",
  windows: "Windows",
  desktop: "Desktop",
  apps: "Apps",
  tips: "Tips",
};

function helpSectionId(tab: HelpContentTab, title: string) {
  const slug = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `help-${tab}-${slug}`;
}

interface HelpSearchHit {
  tab: HelpContentTab;
  section: string;
  item: HelpItem;
  haystack: string;
}

const HELP_SEARCH_INDEX: HelpSearchHit[] = (
  Object.keys(HELP_CONTENT) as HelpContentTab[]
).flatMap((tab) =>
  HELP_CONTENT[tab].flatMap((section) =>
    section.items.map((item) => ({
      tab,
      section: section.title,
      item,
      haystack: (item.kind === "shortcut"
        ? `${item.keys} ${item.desc}`
        : item.text
      ).toLowerCase(),
    }))
  )
);

function HelpTabContent({
  tab,
  isDark,
  highlightId,
}: {
  tab: HelpContentTab;
  isDark: boolean;
  highlightId: string | null;
}) {
  return (
    <div>
      {HELP_CONTENT[tab].map((section) => {
        const id = helpSectionId(tab, section.title);
        const isHighlighted = highlightId === id;
        return (
          <div
            key={section.title}
            id={id}
            className="rounded-md transition-colors duration-500 ease-out"
            style={{
              backgroundColor: isHighlighted
                ? isDark
                  ? "rgba(250, 204, 21, 0.22)"
                  : "rgba(250, 204, 21, 0.38)"
                : "transparent",
              boxShadow: isHighlighted
                ? isDark
                  ? "0 0 0 1px rgba(250, 204, 21, 0.35)"
                  : "0 0 0 1px rgba(202, 138, 4, 0.4)"
                : "none",
              padding: "4px 8px",
              margin: "0 -8px",
            }}
          >
            <HelpSectionHeader title={section.title} isDark={isDark} />
            {section.items.map((item, i) =>
              item.kind === "shortcut" ? (
                <HelpShortcutRow
                  key={i}
                  keys={item.keys}
                  desc={item.desc}
                  isDark={isDark}
                />
              ) : (
                <HelpTip key={i} text={item.text} isDark={isDark} />
              )
            )}
          </div>
        );
      })}
    </div>
  );
}

interface HelpSearchResultsProps {
  query: string;
  isDark: boolean;
  onJump: (tab: HelpContentTab, sectionTitle: string) => void;
}

function HelpSearchResults({ query, isDark, onJump }: HelpSearchResultsProps) {
  const trimmed = query.trim().toLowerCase();
  const textMuted = isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)";
  const textPrimary = isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)";
  const crumb = isDark ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)";
  const hoverBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
  const borderColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)";

  if (!trimmed) {
    return (
      <div
        className="py-10 text-center text-[13px]"
        style={{ color: textMuted }}
      >
        Type to search shortcuts, tips, and app guides.
      </div>
    );
  }

  const hits = HELP_SEARCH_INDEX.filter((h) => h.haystack.includes(trimmed));

  if (hits.length === 0) {
    return (
      <div
        className="py-10 text-center text-[13px]"
        style={{ color: textMuted }}
      >
        No results for "{query}".
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div
        className="mb-2 text-[11px] uppercase tracking-wider"
        style={{ color: textMuted }}
      >
        {hits.length} result{hits.length === 1 ? "" : "s"}
      </div>
      {hits.map((hit, i) => (
        <button
          key={i}
          onClick={() => onJump(hit.tab, hit.section)}
          className="group flex flex-col items-start gap-1 rounded-md px-2 py-2 text-left transition-colors"
          style={{
            borderBottom: `1px solid ${borderColor}`,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = hoverBg;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <div
            className="text-[11px] font-medium uppercase tracking-wider"
            style={{ color: crumb }}
          >
            {HELP_TAB_LABELS[hit.tab]} › {hit.section}
          </div>
          {hit.item.kind === "shortcut" ? (
            <div className="flex w-full items-center justify-between gap-3">
              <span className="text-[13px]" style={{ color: textPrimary }}>
                {hit.item.desc}
              </span>
              <kbd
                className="flex-shrink-0 rounded px-1.5 py-0.5 font-mono text-[11px]"
                style={{
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.06)",
                  color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
                  border: isDark
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid rgba(0,0,0,0.1)",
                }}
              >
                {hit.item.keys}
              </kbd>
            </div>
          ) : (
            <span className="text-[13px]" style={{ color: textPrimary }}>
              {hit.item.text}
            </span>
          )}
        </button>
      ))}
    </div>
  );
}

interface HelpOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab: HelpTab;
  isDark: boolean;
}

function HelpOverlay({
  isOpen,
  onClose,
  initialTab,
  isDark,
}: HelpOverlayProps) {
  const [activeTab, setActiveTab] = useState<HelpTab>(initialTab);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingScrollId, setPendingScrollId] = useState<string | null>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) setActiveTab(initialTab);
  }, [isOpen, initialTab]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!pendingScrollId) return;
    const el = document.getElementById(pendingScrollId);
    if (el && contentRef.current) {
      const container = contentRef.current;
      const elTop = el.getBoundingClientRect().top;
      const containerTop = container.getBoundingClientRect().top;
      container.scrollTo({
        top: container.scrollTop + (elTop - containerTop) - 8,
        behavior: "smooth",
      });
    }
    setPendingScrollId(null);
  }, [pendingScrollId, activeTab]);

  useEffect(() => {
    if (isOpen && activeTab === "search") {
      searchInputRef.current?.focus();
    }
  }, [isOpen, activeTab]);

  useEffect(() => {
    if (!highlightId) return;
    const timer = window.setTimeout(() => setHighlightId(null), 2200);
    return () => window.clearTimeout(timer);
  }, [highlightId]);

  if (!isOpen) return null;

  const tabs: { key: HelpTab; label: string }[] = [
    { key: "search", label: "Search" },
    { key: "shortcuts", label: "Shortcuts" },
    { key: "windows", label: "Windows" },
    { key: "desktop", label: "Desktop" },
    { key: "apps", label: "Apps" },
    { key: "tips", label: "Tips" },
  ];

  const bg = isDark ? "rgba(30, 30, 30, 0.97)" : "rgba(250, 250, 250, 0.97)";
  const borderColor = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)";
  const textPrimary = isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)";

  const handleJump = (tab: HelpContentTab, sectionTitle: string) => {
    const id = helpSectionId(tab, sectionTitle);
    setActiveTab(tab);
    setPendingScrollId(id);
    setHighlightId(id);
  };

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="pointer-events-none fixed inset-0 z-[201] flex items-start justify-center pt-[8vh]">
        <div
          className="pointer-events-auto mx-4 flex w-full max-w-[580px] flex-col overflow-hidden rounded-xl shadow-2xl"
          style={{
            backgroundColor: bg,
            border: `1px solid ${borderColor}`,
            backdropFilter: "blur(20px)",
            maxHeight: "75vh",
          }}
        >
          {/* Header */}
          <div
            className="flex flex-shrink-0 items-center justify-between px-4 py-3"
            style={{ borderBottom: `1px solid ${borderColor}` }}
          >
            <span
              className="text-[15px] font-semibold"
              style={{ color: textPrimary }}
            >
              Help
            </span>
            <button
              onClick={onClose}
              className="flex h-6 w-6 items-center justify-center rounded-full text-xs"
              style={{
                backgroundColor: isDark
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.08)",
                color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)",
              }}
            >
              ✕
            </button>
          </div>

          {/* Tabs */}
          <div
            className="flex flex-shrink-0 gap-0 overflow-x-auto px-3 pt-2"
            style={{ borderBottom: `1px solid ${borderColor}` }}
          >
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className="relative flex-shrink-0 px-3 py-2 text-[13px] font-medium"
                style={{
                  color:
                    activeTab === tab.key
                      ? isDark
                        ? "#fff"
                        : "#000"
                      : isDark
                        ? "rgba(255,255,255,0.45)"
                        : "rgba(0,0,0,0.4)",
                }}
              >
                {tab.label}
                {activeTab === tab.key && (
                  <div
                    className="absolute bottom-0 left-3 right-3 h-[2px] rounded-full"
                    style={{ backgroundColor: isDark ? "#3b82f6" : "#2563eb" }}
                  />
                )}
              </button>
            ))}
          </div>

          {/* Search input row (only on search tab) */}
          {activeTab === "search" && (
            <div
              className="flex-shrink-0 px-4 pt-3"
              style={{ borderBottom: `1px solid ${borderColor}` }}
            >
              <div
                className="mb-3 flex items-center gap-2 rounded-lg px-3 py-2"
                style={{
                  backgroundColor: isDark
                    ? "rgba(255,255,255,0.06)"
                    : "rgba(0,0,0,0.04)",
                  border: `1px solid ${borderColor}`,
                }}
              >
                <span
                  className="text-[13px]"
                  style={{
                    color: isDark
                      ? "rgba(255,255,255,0.4)"
                      : "rgba(0,0,0,0.35)",
                  }}
                >
                  ⌕
                </span>
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search help…"
                  className="flex-1 bg-transparent text-[13px] outline-none"
                  style={{ color: textPrimary }}
                  autoFocus
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="flex h-5 w-5 items-center justify-center rounded-full text-[10px]"
                    style={{
                      backgroundColor: isDark
                        ? "rgba(255,255,255,0.12)"
                        : "rgba(0,0,0,0.08)",
                      color: isDark
                        ? "rgba(255,255,255,0.6)"
                        : "rgba(0,0,0,0.5)",
                    }}
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Content */}
          <div ref={contentRef} className="flex-1 overflow-y-auto px-4 py-3">
            {activeTab === "search" ? (
              <HelpSearchResults
                query={searchQuery}
                isDark={isDark}
                onJump={handleJump}
              />
            ) : (
              <HelpTabContent
                tab={activeTab}
                isDark={isDark}
                highlightId={highlightId}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

/* ==========================================================================
   APP SWITCHER MENU BUTTON
   ========================================================================== */

function AppSwitcherMenuButton({
  isDark,
  isOpen,
  count,
  onClick,
  centered,
}: {
  isDark: boolean;
  isOpen: boolean;
  count: number;
  onClick: () => void;
  /** When true, positions absolutely in the menu bar's center slot. */
  centered: boolean;
}) {
  const bgActive = isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.12)";
  const bgIdle = "transparent";
  const color = isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)";
  return (
    <button
      onClick={onClick}
      aria-label="App switcher"
      aria-pressed={isOpen}
      title={`App Switcher (${navigator.platform.toLowerCase().includes("mac") ? "⌘" : "Ctrl"}+⇧+A)`}
      className={`${centered ? "absolute left-1/2 -translate-x-1/2" : "ml-1"} flex h-[22px] items-center gap-1.5 rounded-md px-2 transition-colors`}
      style={{
        background: isOpen ? bgActive : bgIdle,
        color,
        opacity: count === 0 ? 0.55 : 1,
      }}
      onMouseEnter={(e) => {
        if (!isOpen) e.currentTarget.style.background = bgActive;
      }}
      onMouseLeave={(e) => {
        if (!isOpen) e.currentTarget.style.background = bgIdle;
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
        <g fill="currentColor">
          <rect x="1" y="1" width="4" height="4" rx="1" />
          <rect x="9" y="1" width="4" height="4" rx="1" />
          <rect x="1" y="9" width="4" height="4" rx="1" />
          <rect x="9" y="9" width="4" height="4" rx="1" />
        </g>
      </svg>
      {count > 0 && (
        <span
          className="rounded-full px-1.5 text-[10px] font-semibold leading-4"
          style={{
            background: isDark ? "rgba(255,255,255,0.22)" : "rgba(0,0,0,0.18)",
            color: isDark ? "rgba(255,255,255,0.95)" : "rgba(0,0,0,0.85)",
          }}
        >
          {count}
        </span>
      )}
    </button>
  );
}

/* ==========================================================================
   COMPONENT
   ========================================================================== */

export function MenuBar({
  height = 28,
  onOpenSpotlight,
  onOpenTutorial,
  onTriggerHurricane,
  onTriggerFire,
  onTriggerWar,
  onTriggerBlizzard,
  onTriggerLsd,
  onTriggerTimeTravel,
  onToggleWidgets,
  onToggleAppSwitcher,
  appSwitcherOpen,
  autoHidden,
  onMouseEnter,
  onMouseLeave,
}: MenuBarProps) {
  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [openMenuKey, setOpenMenuKey] = useState<MenuKey | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [helpTab, setHelpTab] = useState<HelpTab>("search");
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [widgetPanelOpen, setWidgetPanelOpen] = useState(false);
  const menuBarRef = useRef<HTMLDivElement>(null);
  const {
    openWindow,
    closeWindow,
    focusWindow,
    minimizeWindow,
    restoreWindow,
    windows,
    isMobile,
  } = useDesktopContext();
  const theme = useThemeOptional();
  const isDark = theme?.isDark ?? true;
  const networkStatus = useNetworkStatus();
  const mediaPermissions = useMediaPermissions();

  // Find active window
  const activeWindow = useMemo((): WindowInstance | null => {
    for (const [, instance] of windows) {
      if (instance.state.status === "open" && instance.state.isActive) {
        return instance;
      }
    }
    return null;
  }, [windows]);

  const activeWindowTitle = activeWindow?.config.title ?? "Scout";

  // Count minimized windows for the app-switcher button badge
  const minimizedCount = useMemo(() => {
    let n = 0;
    for (const { state } of windows.values()) {
      if (state.status === "minimized") n++;
    }
    return n;
  }, [windows]);

  // Update clock every minute
  useEffect(() => {
    const updateTime = () => setCurrentTime(new Date());
    updateTime();

    const now = new Date();
    const msUntilNextMinute =
      (60 - now.getSeconds()) * 1000 - now.getMilliseconds();

    const syncTimeout = setTimeout(() => {
      updateTime();
      const interval = setInterval(updateTime, 60000);
      return () => clearInterval(interval);
    }, msUntilNextMinute);

    return () => clearTimeout(syncTimeout);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuBarRef.current &&
        !menuBarRef.current.contains(event.target as Node)
      ) {
        setOpenMenuKey(null);
      }
    };

    if (openMenuKey !== null) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuKey]);

  const closeMenu = useCallback(() => setOpenMenuKey(null), []);

  const handleMenuClick = useCallback((key: MenuKey) => {
    setOpenMenuKey((prev) => (prev === key ? null : key));
  }, []);

  const handleMenuHover = useCallback((key: MenuKey) => {
    setOpenMenuKey((prev) => (prev !== null ? key : prev));
  }, []);

  // --- Actions ---

  const handleSettingsClick = useCallback(() => {
    const settingsWindow = windows.get(WINDOW_ID.settings);
    if (settingsWindow && settingsWindow.state.status === "open") {
      focusWindow(WINDOW_ID.settings);
    } else {
      openWindow(WINDOW_ID.settings);
    }
  }, [windows, openWindow, focusWindow]);

  const handleSearchClick = useCallback(() => {
    onOpenSpotlight?.();
  }, [onOpenSpotlight]);

  const handleCloseWindow = useCallback(() => {
    if (activeWindow) {
      closeWindow(activeWindow.config.id);
    }
  }, [activeWindow, closeWindow]);

  const handleMinimizeWindow = useCallback(() => {
    if (activeWindow) {
      minimizeWindow(activeWindow.config.id);
    }
  }, [activeWindow, minimizeWindow]);

  const handleZoomWindow = useCallback(() => {
    if (activeWindow) {
      activeWindow.actions.toggleMaximize();
    }
  }, [activeWindow]);

  const handleFullScreen = useCallback(() => {
    if (activeWindow) {
      activeWindow.actions.toggleMaximize();
    }
  }, [activeWindow]);

  const handleToggleTheme = useCallback(() => {
    theme?.toggleTheme();
  }, [theme]);

  const handleFocusActiveWindow = useCallback(() => {
    if (activeWindow) {
      focusWindow(activeWindow.config.id);
    }
  }, [activeWindow, focusWindow]);

  // --- Menu definitions ---

  const openHelp = useCallback((tab: HelpTab) => {
    setHelpTab(tab);
    setHelpOpen(true);
  }, []);

  const fileMenuItems = useMemo(
    (): MenuItem[] => [
      { label: "New Window", shortcut: "⌘N", onClick: handleSearchClick },
      {
        label: "Close Window",
        shortcut: "⌘W",
        onClick: handleCloseWindow,
        disabled: !activeWindow,
      },
    ],
    [handleSearchClick, handleCloseWindow, activeWindow]
  );

  const editMenuItems = useMemo(
    (): MenuItem[] => [
      {
        label: "Undo",
        shortcut: "⌘Z",
        onClick: () => document.execCommand("undo"),
      },
      {
        label: "Redo",
        shortcut: "⇧⌘Z",
        onClick: () => document.execCommand("redo"),
      },
      { divider: true, label: "" },
      {
        label: "Cut",
        shortcut: "⌘X",
        onClick: () => document.execCommand("cut"),
      },
      {
        label: "Copy",
        shortcut: "⌘C",
        onClick: () => document.execCommand("copy"),
      },
      {
        label: "Paste",
        shortcut: "⌘V",
        onClick: () => document.execCommand("paste"),
      },
      {
        label: "Select All",
        shortcut: "⌘A",
        onClick: () => document.execCommand("selectAll"),
      },
    ],
    []
  );

  const viewMenuItems = useMemo(
    (): MenuItem[] => [
      {
        label: "Toggle Full Screen",
        shortcut: "⌃⌘F",
        onClick: handleFullScreen,
        disabled: !activeWindow,
      },
      { divider: true, label: "" },
      {
        label:
          theme?.mode === "system"
            ? "Light Mode"
            : isDark
              ? "System Theme"
              : "Dark Mode",
        onClick: handleToggleTheme,
      },
      { divider: true, label: "" },
      { label: "Widgets", shortcut: "F4", onClick: () => onToggleWidgets?.() },
    ],
    [
      handleFullScreen,
      handleToggleTheme,
      activeWindow,
      isDark,
      theme?.mode,
      onToggleWidgets,
    ]
  );

  const windowMenuItems = useMemo((): MenuItem[] => {
    const items: MenuItem[] = [
      {
        label: "Minimize",
        shortcut: "⌘M",
        onClick: handleMinimizeWindow,
        disabled: !activeWindow,
      },
      { label: "Zoom", onClick: handleZoomWindow, disabled: !activeWindow },
      { divider: true, label: "" },
    ];

    // Dynamic list of open/minimized windows
    for (const [id, instance] of windows) {
      const { state } = instance;
      if (state.status === "open" || state.status === "minimized") {
        const isActive = state.status === "open" && state.isActive;
        const isMinimized = state.status === "minimized";
        items.push({
          label: instance.config.title,
          checked: isActive,
          diamond: isMinimized,
          onClick: () => {
            if (isMinimized) {
              restoreWindow(id);
            }
            focusWindow(id);
          },
        });
      }
    }

    return items;
  }, [
    windows,
    activeWindow,
    handleMinimizeWindow,
    handleZoomWindow,
    focusWindow,
    restoreWindow,
  ]);

  const helpMenuItems = useMemo(
    (): MenuItem[] => [
      { label: "✨ Tutorial", onClick: () => onOpenTutorial?.() },
      { divider: true, label: "" },
      { label: "Search Apps", shortcut: "⇧⌘S", onClick: handleSearchClick },
      { divider: true, label: "" },
      { label: "Search Help", onClick: () => openHelp("search") },
      { label: "Keyboard Shortcuts", onClick: () => openHelp("shortcuts") },
      { label: "Window Management", onClick: () => openHelp("windows") },
      { label: "Desktop & Dock", onClick: () => openHelp("desktop") },
      { label: "App Guide", onClick: () => openHelp("apps") },
      { label: "Tips & Tricks", onClick: () => openHelp("tips") },
    ],
    [handleSearchClick, openHelp, onOpenTutorial]
  );

  const systemMenuItems = useMemo(
    (): MenuItem[] => [
      { label: "Settings...", onClick: handleSettingsClick },
      { label: "Search...", onClick: handleSearchClick },
      // On mobile, show File/Edit/View/Window/Help as submenu entries
      ...(isMobile
        ? [
            { divider: true, label: "" },
            { label: "File", submenu: fileMenuItems },
            { label: "Edit", submenu: editMenuItems },
            { label: "View", submenu: viewMenuItems },
            { label: "Window", submenu: windowMenuItems },
            { label: "Help", submenu: helpMenuItems },
          ]
        : []),
      ...(isMobile
        ? [
            {
              divider: true,
              label: "",
              hidden:
                !onTriggerHurricane &&
                !onTriggerFire &&
                !onTriggerWar &&
                !onTriggerBlizzard &&
                !onTriggerLsd &&
                !onTriggerTimeTravel,
            },
            {
              label: "🎉 Fun Modes",
              hidden:
                !onTriggerHurricane &&
                !onTriggerFire &&
                !onTriggerWar &&
                !onTriggerBlizzard &&
                !onTriggerLsd &&
                !onTriggerTimeTravel,
              submenu: [
                {
                  label: "🌀 Hurricane Mode",
                  onClick: () => onTriggerHurricane?.(),
                  hidden: !onTriggerHurricane,
                },
                {
                  label: "🔥 Fire Mode",
                  onClick: () => onTriggerFire?.(),
                  hidden: !onTriggerFire,
                },
                {
                  label: "💥 War Mode",
                  onClick: () => onTriggerWar?.(),
                  hidden: !onTriggerWar,
                },
                {
                  label: "❄️ Blizzard Mode",
                  onClick: () => onTriggerBlizzard?.(),
                  hidden: !onTriggerBlizzard,
                },
                {
                  label: "🌈 LSD Mode",
                  onClick: () => onTriggerLsd?.(),
                  hidden: !onTriggerLsd,
                },
                {
                  label: "⏳ Theme Time Travel",
                  onClick: () => onTriggerTimeTravel?.(),
                  hidden: !onTriggerTimeTravel,
                },
              ],
            },
          ]
        : [
            { divider: true, label: "" },
            {
              label: "🌀 Hurricane Mode",
              onClick: () => onTriggerHurricane?.(),
              hidden: !onTriggerHurricane,
            },
            {
              label: "🔥 Fire Mode",
              onClick: () => onTriggerFire?.(),
              hidden: !onTriggerFire,
            },
            {
              label: "💥 War Mode",
              onClick: () => onTriggerWar?.(),
              hidden: !onTriggerWar,
            },
            {
              label: "❄️ Blizzard Mode",
              onClick: () => onTriggerBlizzard?.(),
              hidden: !onTriggerBlizzard,
            },
            {
              label: "🌈 LSD Mode",
              onClick: () => onTriggerLsd?.(),
              hidden: !onTriggerLsd,
            },
            {
              label: "⏳ Theme Time Travel",
              onClick: () => onTriggerTimeTravel?.(),
              hidden: !onTriggerTimeTravel,
            },
            {
              divider: true,
              label: "",
              hidden:
                !onTriggerHurricane &&
                !onTriggerFire &&
                !onTriggerWar &&
                !onTriggerBlizzard &&
                !onTriggerLsd &&
                !onTriggerTimeTravel,
            },
          ]),
      {
        label: "Restart",
        onClick: async () => {
          if ("serviceWorker" in navigator) {
            const regs = await navigator.serviceWorker.getRegistrations();
            await Promise.all(regs.map((r) => r.unregister()));
          }
          if ("caches" in window) {
            const keys = await caches.keys();
            await Promise.all(keys.map((k) => caches.delete(k)));
          }
          window.location.reload();
        },
      },
      // Nuke localStorage to reset all persisted state to a clean slate.
      {
        label: "Reset Settings",
        onClick: () => {
          try {
            localStorage.clear();
          } catch {
            // localStorage may be unavailable (private mode, quota errors)
          }
          window.location.reload();
        },
      },
    ],
    [
      handleSettingsClick,
      handleSearchClick,
      onTriggerHurricane,
      onTriggerFire,
      onTriggerWar,
      onTriggerBlizzard,
      onTriggerLsd,
      onTriggerTimeTravel,
      isMobile,
      fileMenuItems,
      editMenuItems,
      viewMenuItems,
      windowMenuItems,
      helpMenuItems,
    ]
  );

  // Map menu keys to their items
  const menuItemsMap: Record<MenuKey, MenuItem[]> = {
    system: systemMenuItems,
    file: fileMenuItems,
    edit: editMenuItems,
    view: viewMenuItems,
    window: windowMenuItems,
    help: helpMenuItems,
  };

  // Render a menu title button
  const renderMenuTitle = (
    key: MenuKey,
    label: React.ReactNode,
    opts?: { bold?: boolean; alignRight?: boolean }
  ) => (
    <div className="relative" key={key}>
      <button
        onClick={() => handleMenuClick(key)}
        onMouseEnter={() => handleMenuHover(key)}
        className="flex h-full items-center rounded-[4px] px-2 text-[13px] transition-colors"
        style={{
          fontWeight: opts?.bold ? 700 : 400,
          color: isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)",
          backgroundColor:
            openMenuKey === key
              ? isDark
                ? "rgba(255,255,255,0.2)"
                : "rgba(0,0,0,0.12)"
              : "transparent",
        }}
        onMouseLeave={(e) => {
          if (openMenuKey !== key) {
            e.currentTarget.style.backgroundColor = "transparent";
          }
        }}
      >
        {label}
      </button>
      {openMenuKey === key && (
        <MenuDropdown
          items={menuItemsMap[key]}
          isDark={isDark}
          onClose={closeMenu}
          alignRight={opts?.alignRight}
          isMobile={isMobile}
        />
      )}
    </div>
  );

  return (
    <div
      ref={menuBarRef}
      data-menubar
      className={`fixed left-0 right-0 top-0 z-[100] flex select-none items-center justify-between px-2 backdrop-blur-xl transition-transform duration-200 ${autoHidden ? "-translate-y-full" : "translate-y-0"}`}
      style={{
        height: `${height}px`,
        backgroundColor: isDark
          ? "rgba(30, 30, 30, 0.85)"
          : "rgba(255, 255, 255, 0.85)",
        borderBottom: isDark
          ? "1px solid rgba(255,255,255,0.1)"
          : "1px solid rgba(0,0,0,0.1)",
        pointerEvents: autoHidden ? "none" : "auto",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Left Side - Menus */}
      <div className="flex h-full items-center">
        {/* Pineapple Logo — always visible */}
        {renderMenuTitle("system", <PineappleLogo />)}

        {/* App Switcher trigger — rendered here on mobile so it doesn't
            overlap with the right-side icons; on desktop it's centered below. */}
        {isMobile && onToggleAppSwitcher && minimizedCount > 0 && (
          <AppSwitcherMenuButton
            isDark={isDark}
            isOpen={!!appSwitcherOpen}
            count={minimizedCount}
            onClick={onToggleAppSwitcher}
            centered={false}
          />
        )}

        {/* App Name — bold, click re-focuses. No dropdown. */}
        <button
          onClick={handleFocusActiveWindow}
          className="hidden h-full items-center px-2 text-[13px] font-bold sm:flex"
          style={{
            color: isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)",
          }}
          onMouseEnter={() => handleMenuHover("system")} // if a menu is open, hovering app name doesn't open anything extra — it just keeps state
        >
          {activeWindowTitle}
        </button>

        {/* Standard menus — hidden on mobile */}
        <div className="hidden h-full items-center sm:flex">
          {renderMenuTitle("file", "File")}
          {renderMenuTitle("edit", "Edit")}
          {renderMenuTitle("view", "View")}
          {renderMenuTitle("window", "Window", { alignRight: false })}
          {renderMenuTitle("help", "Help", { alignRight: true })}
        </div>
      </div>

      {/* Center - App Switcher trigger (desktop only; mobile version is in the left cluster) */}
      {!isMobile && onToggleAppSwitcher && minimizedCount > 0 && (
        <AppSwitcherMenuButton
          isDark={isDark}
          isOpen={!!appSwitcherOpen}
          count={minimizedCount}
          onClick={onToggleAppSwitcher}
          centered
        />
      )}

      {/* Right Side - Network Status & Clock */}
      <div
        className="flex items-center gap-3 text-sm font-medium"
        style={{
          color: isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)",
        }}
      >
        <MediaIndicators
          micPermission={mediaPermissions.micPermission}
          camPermission={mediaPermissions.camPermission}
          micActive={mediaPermissions.micActive}
          camActive={mediaPermissions.camActive}
          isDark={isDark}
        />

        <NetworkIndicator
          isOnline={networkStatus.isOnline}
          effectiveType={networkStatus.effectiveType}
          isDark={isDark}
        />

        <NotificationBell
          isDark={isDark}
          onClick={() => setNotifPanelOpen((prev) => !prev)}
        />

        <button
          onClick={() => {
            if (!isMobile) setWidgetPanelOpen((prev) => !prev);
          }}
          className="flex cursor-pointer items-center"
          style={{
            background: "none",
            border: "none",
            color: "inherit",
            font: "inherit",
            padding: "0 2px",
            borderRadius: 4,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = isDark
              ? "rgba(255,255,255,0.1)"
              : "rgba(0,0,0,0.06)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <span className="hidden sm:inline">
            {formatMacOSTime(currentTime)}
          </span>
          <span className="sm:hidden">
            {formatMacOSTime(currentTime, true)}
          </span>
        </button>
      </div>

      {/* Notification Panel (bell click) */}
      <NotificationPanel
        isOpen={notifPanelOpen}
        onClose={() => setNotifPanelOpen(false)}
        isDark={isDark}
      />

      {/* Notification + Widget Panel (time click) */}
      <NotificationWidgetPanel
        isOpen={widgetPanelOpen}
        onClose={() => setWidgetPanelOpen(false)}
        isDark={isDark}
      />

      {/* Help Overlay */}
      <HelpOverlay
        isOpen={helpOpen}
        onClose={() => setHelpOpen(false)}
        initialTab={helpTab}
        isDark={isDark}
      />
    </div>
  );
}

/* ==========================================================================
   ICONS
   ========================================================================== */

function PineappleLogo() {
  return (
    <svg
      width="14"
      height="18"
      viewBox="0 0 56 80"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className="inline-block"
      aria-label="Pineapple"
    >
      <defs>
        <mask id="pineapple-texture">
          <rect width="56" height="80" fill="white" />
          {/* Diamond cross-hatch cut into body */}
          <line
            x1="8"
            y1="42"
            x2="28"
            y2="28"
            stroke="black"
            strokeWidth="2.5"
          />
          <line
            x1="8"
            y1="58"
            x2="48"
            y2="28"
            stroke="black"
            strokeWidth="2.5"
          />
          <line
            x1="14"
            y1="72"
            x2="48"
            y2="42"
            stroke="black"
            strokeWidth="2.5"
          />
          <line
            x1="28"
            y1="76"
            x2="48"
            y2="58"
            stroke="black"
            strokeWidth="2.5"
          />
          <line
            x1="48"
            y1="42"
            x2="28"
            y2="28"
            stroke="black"
            strokeWidth="2.5"
          />
          <line
            x1="48"
            y1="58"
            x2="8"
            y2="28"
            stroke="black"
            strokeWidth="2.5"
          />
          <line
            x1="42"
            y1="72"
            x2="8"
            y2="42"
            stroke="black"
            strokeWidth="2.5"
          />
          <line
            x1="28"
            y1="76"
            x2="8"
            y2="58"
            stroke="black"
            strokeWidth="2.5"
          />
        </mask>
      </defs>
      {/* Crown leaves — 5 pointed leaves fanning out */}
      <path d="M28 28 C26 16, 18 4, 14 0 C16 6, 18 14, 22 24Z" />
      <path d="M28 26 C27 14, 27 6, 28 0 C29 6, 29 14, 28 26Z" />
      <path d="M28 28 C30 16, 38 4, 42 0 C40 6, 38 14, 34 24Z" />
      <path d="M26 28 C22 18, 12 10, 6 6 C10 12, 20 20, 24 26Z" />
      <path d="M30 28 C34 18, 44 10, 50 6 C46 12, 36 20, 32 26Z" />
      {/* Body with diamond texture cutouts */}
      <rect
        x="8"
        y="28"
        width="40"
        height="48"
        rx="18"
        mask="url(#pineapple-texture)"
      />
    </svg>
  );
}

/* ==========================================================================
   MEDIA INDICATORS COMPONENT
   ========================================================================== */

interface MediaIndicatorsProps {
  micPermission: MediaPermissionState;
  camPermission: MediaPermissionState;
  micActive: boolean;
  camActive: boolean;
  isDark: boolean;
}

function getMediaColor(
  permission: MediaPermissionState,
  active: boolean,
  isDark: boolean
): string {
  if (active) return "#22c55e"; // green — in use
  if (permission === "granted")
    return isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)"; // white/black — permitted
  return isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.3)"; // dim — not granted
}

function getMediaTooltip(
  label: string,
  permission: MediaPermissionState,
  active: boolean
): string {
  if (active) return `${label}: In use`;
  if (permission === "granted") return `${label}: Permitted`;
  if (permission === "denied") return `${label}: Denied`;
  return `${label}: Not requested`;
}

function MediaIndicators({
  micPermission,
  camPermission,
  micActive,
  camActive,
  isDark,
}: MediaIndicatorsProps) {
  const micColor = getMediaColor(micPermission, micActive, isDark);
  const camColor = getMediaColor(camPermission, camActive, isDark);
  const micTooltip = getMediaTooltip("Microphone", micPermission, micActive);
  const camTooltip = getMediaTooltip("Camera", camPermission, camActive);
  const showMicStrike =
    micPermission === "prompt" || micPermission === "denied";
  const showCamStrike =
    camPermission === "prompt" || camPermission === "denied";

  return (
    <div className="flex items-center gap-2">
      {/* Microphone */}
      <div className="flex items-center justify-center" title={micTooltip}>
        <svg width="10" height="14" viewBox="0 0 10 14" fill="none">
          <rect x="2.5" y="0.5" width="5" height="8" rx="2.5" fill={micColor} />
          <path
            d="M1 6.5C1 9 3 10.5 5 10.5C7 10.5 9 9 9 6.5"
            stroke={micColor}
            strokeWidth="1.2"
            strokeLinecap="round"
            fill="none"
          />
          <line
            x1="5"
            y1="10.5"
            x2="5"
            y2="13"
            stroke={micColor}
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          <line
            x1="3"
            y1="13"
            x2="7"
            y2="13"
            stroke={micColor}
            strokeWidth="1.2"
            strokeLinecap="round"
          />
          {showMicStrike && (
            <line
              x1="1"
              y1="12.5"
              x2="9"
              y2="1"
              stroke={micColor}
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          )}
        </svg>
      </div>

      {/* Camera */}
      <div className="flex items-center justify-center" title={camTooltip}>
        <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
          <rect x="0.5" y="1.5" width="9" height="7" rx="1.5" fill={camColor} />
          <path d="M9.5 3.5L13 1.5V8.5L9.5 6.5" fill={camColor} />
          {showCamStrike && (
            <line
              x1="1"
              y1="9"
              x2="13"
              y2="1"
              stroke={camColor}
              strokeWidth="1.3"
              strokeLinecap="round"
            />
          )}
        </svg>
      </div>
    </div>
  );
}

/* ==========================================================================
   NETWORK INDICATOR COMPONENT
   ========================================================================== */

interface NetworkIndicatorProps {
  isOnline: boolean;
  effectiveType: NetworkEffectiveType | null;
  isDark: boolean;
}

function getSignalStrength(effectiveType: NetworkEffectiveType | null): number {
  if (!effectiveType) return 3;
  switch (effectiveType) {
    case "slow-2g":
      return 0;
    case "2g":
      return 1;
    case "3g":
      return 2;
    case "4g":
      return 3;
    default:
      return 3;
  }
}

function getNetworkTooltip(
  isOnline: boolean,
  effectiveType: NetworkEffectiveType | null
): string {
  if (!isOnline) return "Offline";
  if (!effectiveType) return "Online";

  const typeLabels: Record<NetworkEffectiveType, string> = {
    "slow-2g": "Very slow connection",
    "2g": "Slow connection",
    "3g": "Good connection",
    "4g": "Fast connection",
  };
  return typeLabels[effectiveType] || "Online";
}

function NetworkIndicator({
  isOnline,
  effectiveType,
  isDark,
}: NetworkIndicatorProps) {
  const strength = isOnline ? getSignalStrength(effectiveType) : 0;
  const tooltip = getNetworkTooltip(isOnline, effectiveType);

  const activeColor = isDark ? "rgba(255,255,255,0.9)" : "rgba(0,0,0,0.85)";
  const inactiveColor = isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.2)";
  const offlineColor = "#ef4444";

  if (!isOnline) {
    return (
      <div className="flex items-center justify-center" title={tooltip}>
        <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
          <path
            d="M1 3.5C3.5 1 10.5 1 13 3.5"
            stroke={offlineColor}
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.4"
          />
          <path
            d="M3 5.5C4.5 4 9.5 4 11 5.5"
            stroke={offlineColor}
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.4"
          />
          <path
            d="M5 7.5C6 6.75 8 6.75 9 7.5"
            stroke={offlineColor}
            strokeWidth="1.5"
            strokeLinecap="round"
            opacity="0.4"
          />
          <circle cx="7" cy="9" r="1" fill={offlineColor} opacity="0.4" />
          <line
            x1="2"
            y1="9"
            x2="12"
            y2="1"
            stroke={offlineColor}
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center" title={tooltip}>
      <svg width="14" height="10" viewBox="0 0 14 10" fill="none">
        <path
          d="M1 3.5C3.5 1 10.5 1 13 3.5"
          stroke={strength >= 3 ? activeColor : inactiveColor}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M3 5.5C4.5 4 9.5 4 11 5.5"
          stroke={strength >= 2 ? activeColor : inactiveColor}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <path
          d="M5 7.5C6 6.75 8 6.75 9 7.5"
          stroke={strength >= 1 ? activeColor : inactiveColor}
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="7" cy="9" r="1" fill={activeColor} />
      </svg>
    </div>
  );
}

export default MenuBar;
